"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronDown, X } from "lucide-react";
import { navigation, type NavItem } from "@/data/navigation";
import DeployButton from "@/components/ui/DeployButton";
import ThemeToggle from "@/components/ui/ThemeToggle";
import Logo from "@/components/ui/Logo";
import { gsap, useGSAP } from "@/lib/gsap";

interface MobileNavProps {
  onClose: () => void;
}

/* A single expanded sub-item — label, optional description + badge,
   plus any nested child links rendered as indented siblings. */
function MobileMenuItem({ item, onClose }: { item: NavItem; onClose: () => void }) {
  return (
    <div>
      <Link
        href={item.href}
        onClick={onClose}
        className="block rounded-lg px-3 py-2.5 text-text-secondary hover:bg-white/5 hover:text-white"
      >
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium">{item.label}</span>
          {item.badge && (
            <span className="rounded bg-brand-purple/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-purple-light">
              {item.badge}
            </span>
          )}
        </span>
        {item.description && (
          <span className="mt-0.5 block text-xs text-text-muted">{item.description}</span>
        )}
      </Link>
      {item.children && item.children.length > 0 && (
        <div className="ml-3 border-l border-border-subtle/60 pl-3">
          {item.children.map((child) => (
            <Link
              key={child.href}
              href={child.href}
              onClick={onClose}
              className="block rounded-md px-3 py-2 text-xs text-text-secondary hover:text-white"
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MobileNav({ onClose }: MobileNavProps) {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const pathname = usePathname();
  const closeRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Focus close button on mount + Escape to close + Tab focus trap
  useEffect(() => {
    closeRef.current?.focus();
    // Lock body scroll while drawer open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const getFocusable = (): HTMLElement[] => {
      if (!drawerRef.current) return [];
      return Array.from(
        drawerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute("inert") && el.offsetParent !== null);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const focusable = getFocusable();
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // ── GSAP: Drawer entrance animation ──────────────────────────────────
  useGSAP(
    () => {
      if (!drawerRef.current) return;

      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        // Backdrop fade
        gsap.from(drawerRef.current!, {
          autoAlpha: 0,
          duration: 0.25,
          ease: "power2.out",
        });

        // Header slides down
        gsap.from(".mobile-nav-header", {
          y: -20,
          autoAlpha: 0,
          duration: 0.3,
          delay: 0.1,
          ease: "power3.out",
        });

        // Nav links stagger in from left
        gsap.from(".mobile-nav-link", {
          x: -30,
          autoAlpha: 0,
          stagger: 0.04,
          duration: 0.35,
          delay: 0.15,
          ease: "power3.out",
        });

        // CTAs slide up
        gsap.from(".mobile-nav-ctas", {
          y: 30,
          autoAlpha: 0,
          duration: 0.4,
          delay: 0.3,
          ease: "power3.out",
        });
      });
    },
    { scope: drawerRef },
  );

  const toggleGroup = (label: string) => {
    setOpenGroup((prev) => (prev === label ? null : label));
  };

  const megaKeys = Object.keys(navigation.megaMenu);

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <div
      ref={drawerRef}
      id="mobile-nav-drawer"
      className="fixed inset-0 z-50 flex flex-col overflow-y-auto overscroll-contain bg-bg-base"
      style={{
        paddingTop: "max(env(safe-area-inset-top), 0px)",
        paddingBottom: "max(env(safe-area-inset-bottom), 0px)",
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Mobile navigation"
    >
      {/* Header */}
      <div className="mobile-nav-header flex items-center justify-between border-b border-border-subtle px-6 py-4">
        <Link href="/" onClick={onClose} className="shrink-0">
          <Logo width={148} height={28} className="h-7 w-auto" />
        </Link>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <button
            ref={closeRef}
            onClick={onClose}
            aria-label="Close menu"
            className="rounded-lg p-2 text-text-secondary hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/60 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Nav links */}
      <nav className="flex-1 overflow-y-auto px-4 py-6" aria-label="Mobile navigation">
        {navigation.mainLinks.map((link) => {
          const hasMega = megaKeys.includes(link.label);

          if (hasMega) {
            const group = navigation.megaMenu[link.label];
            const isOpen = openGroup === link.label;
            return (
              <div key={link.label} className="mobile-nav-link">
                <button
                  onClick={() => toggleGroup(link.label)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-left text-base text-text-secondary hover:bg-white/5 hover:text-white"
                >
                  {link.label}
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {isOpen && (
                  <div className="ml-4 mt-1 space-y-0.5 border-l border-border-subtle pl-4">
                    {group.sections?.map((section) => (
                      <div key={section.title} className="pt-1 first:pt-0">
                        <p className="flex items-center gap-2 px-3 pb-1 pt-2 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">
                          <span className="h-px w-4 shrink-0 bg-brand-purple/60" aria-hidden />
                          {section.title}
                        </p>
                        {section.items.map((item) => (
                          <MobileMenuItem key={item.href} item={item} onClose={onClose} />
                        ))}
                      </div>
                    ))}
                    {group.items?.map((item) => (
                      <MobileMenuItem key={item.href} item={item} onClose={onClose} />
                    ))}
                    {group.viewAllHref && (
                      <Link
                        href={group.viewAllHref}
                        onClick={onClose}
                        className="block rounded-lg px-3 py-2.5 text-sm font-medium text-brand-purple-light hover:bg-brand-purple/10"
                      >
                        {group.viewAllLabel ?? "View all"} →
                      </Link>
                    )}
                    {group.cta && (
                      <Link
                        href={group.cta.href}
                        onClick={onClose}
                        {...(group.cta.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        data-cta="deploy-now"
                        data-cta-location="mobile-nav"
                        className="mt-2 flex items-center justify-between rounded-lg border border-brand-purple/30 bg-brand-purple/10 px-3 py-2.5"
                      >
                        <span>
                          <span className="block text-sm font-semibold text-white">{group.cta.title}</span>
                          {group.cta.description && (
                            <span className="block text-xs text-text-muted">{group.cta.description}</span>
                          )}
                        </span>
                        <span className="text-xs font-semibold text-brand-purple-light">
                          {group.cta.buttonLabel} →
                        </span>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            );
          }

          const active = isActive(link.href);
          return (
            <Link
              key={link.href}
              href={link.href}
              onClick={onClose}
              className={`mobile-nav-link block rounded-lg px-4 py-3 text-base transition-colors ${
                active
                  ? "font-medium text-white"
                  : "text-text-secondary hover:bg-white/5 hover:text-white"
              }`}
            >
              {link.label}
            </Link>
          );
        })}
      </nav>

      {/* CTAs */}
      <div className="mobile-nav-ctas border-t border-border-subtle px-6 py-5 space-y-3">
        <Link
          href="/contact-us"
          onClick={onClose}
          data-cta="book-a-demo"
          data-cta-location="mobile-nav"
          className="flex w-full items-center justify-center rounded-lg border border-border-subtle px-4 py-3 text-sm font-medium text-text-secondary transition-colors hover:border-brand-purple/50 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/60"
        >
          Book a Demo
        </Link>
        <DeployButton size="lg" location="mobile-nav" className="w-full" />
      </div>
    </div>
  );
}
